import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  AlreadyImportedGroup,
  CenterTeacher,
  CheckboxItemNode,
  ErrorModel,
  Group,
  SelectedStudent,
  StudentsGroup,
  TeacherGroup,
} from '@tekman/onmat/models';
import * as GroupsActions from './groups.actions';
import { CloneUtility } from '@tekman/shared/utils';

export const GROUPS_FEATURE_KEY = 'groups';

export interface State extends EntityState<Group> {
  loaded: boolean;
  loadedSaveGroup?: boolean;
  selectedGroup: Group | null;
  selectedStudent: SelectedStudent;
  error?: ErrorModel;
  students: StudentsGroup[];
  failedStudents?: StudentsGroup[];
  failedTeachers?: TeacherGroup[];
  successStudents?: StudentsGroup[];
  teams: CheckboxItemNode[];
  teachers: CenterTeacher[];
  editableGroup?: Group;
  alreadyImportedGroup?: AlreadyImportedGroup;
  editableStudents?: StudentsGroup[];
  allTeachers?: TeacherGroup[];
}

export interface GroupsPartialState {
  readonly [GROUPS_FEATURE_KEY]: State;
}

export const groupsAdapter: EntityAdapter<Group> = createEntityAdapter<Group>({
  selectId: group => group.id,
});

export const initialState: State = groupsAdapter.getInitialState({
  selectedGroup: null,
  selectedStudent: null,
  loaded: false,
  loadedSaveGroup: false,
  students: [],
  teams: [],
  licenses: [],
  teachers: [],
});

const groupsReducer = createReducer(
  initialState,
  on(GroupsActions.loadGroups, state => ({ ...state, loaded: false, loadedSaveGroup: false, error: null })),
  on(GroupsActions.loadGroupsSuccess, (state, { groups }) =>
    groupsAdapter.setAll(groups, {
      ...state,
      loaded: true,
    })
  ),
  on(GroupsActions.selectGroup, (state, { selectedGroup }) => ({ ...state, selectedGroup })),
  on(GroupsActions.saveGroupSuccess, (state, { group, groupId, failedStudents, successStudents, failedTeachers }) => {
    const newState = {
      ...state,
      failedStudents,
      successStudents,
      failedTeachers,
      loadedSaveGroup: true,
    };

    if (group) {
      return groupsAdapter.upsertOne(group, newState);
    } else if (!group) {
      return groupsAdapter.removeOne(groupId, newState);
    }
  }),
  on(GroupsActions.removeGroupSuccess, (state, { groupId }) => groupsAdapter.removeOne(groupId, { ...state })),
  on(GroupsActions.selectStudent, (state, { selectedStudent }) => ({ ...state, selectedStudent })),
  on(GroupsActions.loadTeamsSuccess, GroupsActions.selectedStudentSuccess, GroupsActions.updatedTeamsSuccess, (state, { teams }) => ({
    ...state,
    teams,
  })),
  on(GroupsActions.loadGroupsFailure, (state, { error }) => ({
    ...state,
    selectedGroup: null,
    error,
  })),
  on(GroupsActions.loadStudentsGroupSuccess, (state, { students }) => ({ ...state, students })),
  on(GroupsActions.loadItinerarySuccess, (state, { itinerary }) => ({
    ...state,
    selectedGroup: {
      ...state.selectedGroup,
      itinerary: CloneUtility.deepClone(itinerary),
    },
  })),
  on(GroupsActions.loadProgressSuccess, (state, { progress }) => ({
    ...state,
    selectedGroup: {
      ...state.selectedGroup,
      progress: { stepId: progress },
    },
  })),
  on(GroupsActions.loadTeachersSuccess, (state, { teachers }) => ({
    ...state,
    teachers,
    loaded: true,
  })),
  on(GroupsActions.loadTeachers, state => ({
    ...state,
    teachers: undefined,
    loaded: false,
  })),
  on(GroupsActions.saveTeachersSuccess, (state, { failedTeachers }) => ({
    ...state,
    failedTeachers: failedTeachers,
    loaded: true,
  })),
  on(GroupsActions.deleteStudentSuccess, (state, { id }) => ({
    ...state,
    editableStudents: [...state.editableStudents.filter(student => student.id !== id)],
    loaded: true,
  })),
  on(GroupsActions.inviteStudentSuccess, (state, { students, failedStudents }) => ({
    ...state,
    editableStudents: [...state.editableStudents, ...students],
    failedStudents: failedStudents,
    loaded: true,
  })),
  on(GroupsActions.loadEditableTeachersSuccess, (state, { teachers }) => ({
    ...state,
    allTeachers: teachers,
    loaded: true,
  })),
  on(GroupsActions.loadEditableGroupSuccess, (state, { group }) => ({
    ...state,
    editableGroup: group,
    editableStudents: group.students,
    loaded: true,
  })),
  on(GroupsActions.updateStepCompletedSuccess, (state, { stepId, isCompleted }) => ({
    ...state,
    loaded: true,
    selectedGroup: {
      ...state.selectedGroup,
      itinerary: state.selectedGroup.itinerary
        ? {
            ...state.selectedGroup.itinerary,
            timePeriods: state.selectedGroup.itinerary.timePeriods?.map(tp => ({
              ...tp,
              steps: tp.steps?.map(step => (step.id === stepId ? { ...step, isCompleted } : step)),
            })),
          }
        : state.selectedGroup.itinerary,
    },
  })),
  on(GroupsActions.resetAlerts, state => ({
    ...state,
    failedTeachers: null,
    successStudents: null,
    failedStudents: null,
  })),
  on(
    GroupsActions.loadStudentsGroupFailure,
    GroupsActions.selectedStudentFailure,
    GroupsActions.loadTeamsFailure,
    GroupsActions.removeGroupFailure,
    GroupsActions.loadItineraryFailure,
    GroupsActions.loadProgressFailure,
    GroupsActions.saveTeachersFailure,
    (state, { error }) => ({
      ...state,
      error,
    })
  ),
  on(GroupsActions.saveGroupFailure, state => ({
    ...state,
    editableGroup: undefined,
    loadedSaveGroup: true,
  })),
  // `loadedSaveGroup` is deliberately left untouched: the import workflow reacts to
  // `alreadyImportedExternalGroupId` instead, so the regular "saved" flow does not run too.
  on(GroupsActions.saveGroupAlreadyImported, (state, { alreadyImportedGroup }) => ({
    ...state,
    editableGroup: undefined,
    alreadyImportedGroup,
  })),
  on(GroupsActions.resetSaveGroupAlreadyImported, state => ({
    ...state,
    alreadyImportedGroup: null,
  })),
  on(GroupsActions.removeGroups, state => initialState),
  on(GroupsActions.removeFailedStudentsImported, state => initialState),
  on(GroupsActions.loadEditableGroup, state => ({
    ...state,
    editableGroup: undefined,
    loaded: true,
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return groupsReducer(state, action);
}
